import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import RegalarModal from '../components/RegalarModal';
import CitaSeguraModal from '../components/CitaSeguraModal';
import BadgeVerificado from '../components/BadgeVerificado';
import PerfilCompleto from '../components/PerfilCompleto';
import '../styles/Extras.css';
import { comoLlegar } from '../lib/mapas';
import { lugarDe } from '../lib/ubicacion';
import '../styles/ChatPage.css';

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

// ── Read receipt ticks ─────────────────────────────────────────────────────
function ReadTick({ readAt }) {
  if (readAt) {
    return <span className="read-tick read-tick--read" title={`Leído ${timeAgo(readAt)}`}>✓✓</span>;
  }
  return <span className="read-tick read-tick--sent" title="Enviado">✓</span>;
}

export default function ChatPage() {
  const { matchId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toasts, error, success } = useToast();

  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('Comportamiento inapropiado');
  const [blockConfirm, setBlockConfirm] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [unblockConfirm, setUnblockConfirm] = useState(false);
  const [regalarOpen, setRegalarOpen] = useState(false);
  const [citaOpen, setCitaOpen] = useState(false);
  const [perfilOpen, setPerfilOpen] = useState(false);
  const [lugar, setLugar] = useState(null);
  const [lugarCerrado, setLugarCerrado] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);
  const selectedRef = useRef(null);

  // Lugar recomendado para la primera cita (local patrocinado de Cupido Descuentos)
  useEffect(() => { api.lugarRecomendado().then(d => setLugar(d.lugar)).catch(() => {}); }, []);

  // Keep selectedRef in sync so polling closure always has latest matchId
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  // ── Cargar matches ─────────────────────────────────────────────────────
  const loadMatches = useCallback(async () => {
    try {
      const data = await api.getMatches();
      setMatches(data.matches || []);
      return data.matches || [];
    } catch (e) {
      error('Error cargando tus conexiones.');
      return [];
    } finally {
      setLoadingMatches(false);
    }
  }, [error]);

  useEffect(() => {
    loadMatches().then(list => {
      if (matchId) {
        const found = list.find(m => m.matchId === matchId);
        if (found) setSelected(found);
      }
    });
  }, [matchId, loadMatches]);

  // ── Cargar mensajes + mark as read ─────────────────────────────────────
  const loadMessages = useCallback(async (mId, markRead = false) => {
    if (!mId) return;
    try {
      const data = await api.getMessages(mId);
      setMessages(data.messages || []);
      // Mark partner messages as read when opening or polling
      if (markRead) {
        api.markMessagesRead(mId).catch(() => {});
      }
    } catch (e) {
      // silent on poll errors
    }
  }, []);

  // Verificar estado de bloqueo al seleccionar un match
  useEffect(() => {
    if (!selected) return;
    api.getBlockStatus(selected.partner.id)
      .then(data => setIsBlocked(data.blocked))
      .catch(() => setIsBlocked(false));
  }, [selected]);

  useEffect(() => {
    if (!selected) return;
    setLoadingMessages(true);
    loadMessages(selected.matchId, true).finally(() => setLoadingMessages(false));

    // Poll every 5s; mark read on each poll so read_at updates flow back
    pollRef.current = setInterval(() => {
      const cur = selectedRef.current;
      if (cur) loadMessages(cur.matchId, true);
    }, 5000);

    return () => clearInterval(pollRef.current);
  }, [selected, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Enviar mensaje ─────────────────────────────────────────────────────
  const sendMessage = async () => {
    const text = input.trim();
    if (!text || !selected || sending) return;
    setSending(true);
    setInput('');
    try {
      const data = await api.sendMessage(selected.matchId, text);
      setMessages(prev => [...prev, data.message]);
    } catch (e) {
      error(e.message || 'Error enviando mensaje.');
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  // ── Bloqueo / Desbloqueo ────────────────────────────────────────────────
  const handleBlock = async () => {
    if (!selected) return;
    try {
      await api.blockUser(selected.partner.id);
      setIsBlocked(true);
      setBlockConfirm(false);
      success(`Bloqueaste a ${selected.partner.name}.`);
    } catch (e) {
      error(e.message || 'Error al bloquear.');
    }
  };

  const handleUnblock = async () => {
    if (!selected) return;
    try {
      await api.unblockUser(selected.partner.id);
      setIsBlocked(false);
      setUnblockConfirm(false);
      success(`Desbloqueaste a ${selected.partner.name}. Ya pueden enviarse mensajes.`);
    } catch (e) {
      error(e.message || 'Error al desbloquear.');
    }
  };

  // ── Reporte ─────────────────────────────────────────────────────────────
  const handleReport = async () => {
    if (!selected) return;
    try {
      await api.createReport({ reportedId: selected.partner.id, reason: reportReason });
      success('Reporte enviado. Revisaremos el caso.');
      setReportOpen(false);
      navigate('/explorar');
    } catch (e) {
      error(e.message || 'Error enviando reporte.');
    }
  };

  const selectMatch = (m) => {
    setSelected(m);
    setMessages([]);
    setIsBlocked(false);
    navigate(`/chat/${m.matchId}`);
  };

  // ── Unread count helper ─────────────────────────────────────────────────
  const unreadCount = (m) => {
    // We don't have per-match unread counts from the matches list endpoint,
    // but we mark read when we open — so just show a dot if lastMessage exists
    // and it's from the partner (simple heuristic)
    if (!m.lastMessage) return 0;
    if (m.lastMessage.from_user === user.id) return 0;
    if (m.lastMessage.read_at) return 0;
    return 1;
  };

  return (
    <div className="chat-page">
      <Navbar />
      <ToastContainer toasts={toasts} />

      <div className={`chat-layout ${selected ? 'chat-layout--conversacion' : ''}`}>
        {/* SIDEBAR */}
        <aside className="chat-sidebar">
          <h2 className="chat-sidebar-title">💕 Mis conexiones</h2>
          {loadingMatches ? (
            <div style={{ padding: 20, color: 'var(--texto-muted)', fontSize: 14 }}>Cargando...</div>
          ) : matches.length === 0 ? (
            <div className="no-matches-chat">
              <p>Todavía no tenés conexiones.</p>
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/explorar')}>
                Explorar perfiles
              </button>
            </div>
          ) : (
            <ul className="match-list">
              {matches.map(m => (
                <li key={m.matchId}
                  className={`match-item ${selected?.matchId === m.matchId ? 'active' : ''}`}
                  onClick={() => selectMatch(m)}>
                  <div className="match-item-avatar">
                    {m.partner?.foto_path
                      ? <img src={m.partner.foto_path} alt={m.partner.name} />
                      : <span>{(m.partner?.name || 'U')[0].toUpperCase()}</span>
                    }
                    {unreadCount(m) > 0 && <span className="match-unread-dot" />}
                  </div>
                  <div className="match-item-info">
                    <div className="match-item-name">{m.partner?.name}<BadgeVerificado show={m.partner?.verificado} dorado={m.partner?.verif_dorado} /></div>
                    {m.partner?.nota && <div className="match-item-nota">📝 {m.partner.nota}</div>}
                    <div className="match-item-preview">
                      {m.lastMessage
                        ? (m.lastMessage.from_user === user.id ? 'Vos: ' : '') + m.lastMessage.text.slice(0, 30) + (m.lastMessage.text.length > 30 ? '…' : '')
                        : '¡Empezá la conversación!'}
                    </div>
                  </div>
                  {m.lastMessage && (
                    <div className="match-item-time">{timeAgo(m.lastMessage.created_at)}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* CHAT MAIN */}
        <main className="chat-main">
          {!selected ? (
            <div className="chat-empty">
              <div className="chat-empty-icon">💬</div>
              <h2>Elegí una conexión para chatear</h2>
              <p>Solo podés chatear con personas con las que el gusto fue mutuo.</p>
            </div>
          ) : (
            <>
              <div className="chat-header">
                <button className="chat-volver" onClick={() => { setSelected(null); navigate('/chat'); }} aria-label="Volver a mis conexiones">←</button>
                <div className="chat-header-avatar chat-header-ver" onClick={() => setPerfilOpen(true)} role="button" tabIndex={0} title="Ver perfil completo">
                  {selected.partner?.foto_path
                    ? <img src={selected.partner.foto_path} alt={selected.partner.name} />
                    : <span>{(selected.partner?.name || 'U')[0].toUpperCase()}</span>
                  }
                </div>
                <div className="chat-header-info chat-header-ver" onClick={() => setPerfilOpen(true)} role="button" tabIndex={0} title="Ver perfil completo">
                  <div className="chat-header-name">{selected.partner?.name}<BadgeVerificado show={selected.partner?.verificado} dorado={selected.partner?.verif_dorado} /></div>
                  <div className="chat-header-dept">📍 {lugarDe(selected.partner)}</div>
                  {selected.partner?.nota && <div className="chat-header-nota">📝 “{selected.partner.nota}”</div>}
                  <div className="chat-header-ver-texto">👤 Ver perfil completo</div>
                </div>
                <button className="btn btn-sm btn-outline" style={{ marginRight: 6 }} disabled={isBlocked}
                  title="Avisale a alguien de confianza dónde y con quién estás" onClick={() => setCitaOpen(true)}>
                  🛡️ Cita segura
                </button>
                <button className="btn btn-sm btn-dorado" style={{ marginRight: 6 }} disabled={isBlocked}
                  title={isBlocked ? 'No podés regalar a alguien bloqueado' : 'Mandale un regalo'}
                  onClick={() => setRegalarOpen(true)}>
                  🎁 Regalar
                </button>
                <button className="btn btn-sm chat-report-btn" onClick={() => setReportOpen(true)}>
                  ⚠️ Reportar
                </button>
                {isBlocked ? (
                  <button className="btn btn-sm" style={{ marginLeft: 6, background: 'rgba(80,180,80,0.15)', color: '#6dbf6d', border: '1px solid rgba(80,180,80,0.3)' }} onClick={() => setUnblockConfirm(true)}>
                    ✅ Desbloquear
                  </button>
                ) : (
                  <button className="btn btn-sm" style={{ marginLeft: 6, background: 'rgba(100,100,100,0.15)', color: 'var(--texto-suave)', border: '1px solid var(--borde)' }} onClick={() => setBlockConfirm(true)}>
                    🚫 Bloquear
                  </button>
                )}
              </div>

              <div className="chat-messages" style={isBlocked ? { opacity: 0.45, filter: 'grayscale(60%)', pointerEvents: 'none' } : {}}>
                {loadingMessages ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--texto-muted)' }}>Cargando mensajes...</div>
                ) : messages.length === 0 ? (
                  <div className="chat-start-hint">
                    <span>🧉</span>
                    <p>¡Conexión con {selected.partner?.name}! ¡Escribile algo!</p>
                  </div>
                ) : (
                  messages.map((msg, idx) => {
                    const isMine = msg.from_user === user.id;
                    // Show read tick only on last message sent by me
                    const isLastMine = isMine && messages.slice(idx + 1).every(m => m.from_user !== user.id);
                    return (
                      <div key={msg.id} className={`message ${isMine ? 'mine' : 'theirs'}`}>
                        <div className="message-bubble">{msg.text}</div>
                        <div className="message-meta">
                          <span className="message-time">{timeAgo(msg.created_at)}</span>
                          {isMine && isLastMine && <ReadTick readAt={msg.read_at} />}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>

              {isBlocked ? (
                <div style={{
                  padding: '14px 20px', background: 'rgba(120,120,120,0.1)', borderTop: '1px solid rgba(120,120,120,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12
                }}>
                  <span style={{ color: 'rgba(160,160,160,0.7)', fontSize: 14 }}>
                    🚫 Bloqueaste a {selected.partner?.name}. No pueden enviarse mensajes.
                  </span>
                  <button
                    onClick={() => setUnblockConfirm(true)}
                    style={{ background: 'rgba(80,180,80,0.15)', color: '#6dbf6d', border: '1px solid rgba(80,180,80,0.3)', borderRadius: 20, padding: '6px 14px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Desbloquear
                  </button>
                </div>
              ) : (
                <>
                {lugar && !lugarCerrado && messages.length < 40 && (
                  <div className="lugar-recomendado">
                    <span className="lugar-recomendado-texto">
                      📍 <strong>Lugar recomendado para la primera cita:</strong> {lugar.nombre_local} · {lugar.descuento}
                    </span>
                    {lugar.direccion && <a href={comoLlegar(lugar.direccion, lugar.nombre_local)} target="_blank" rel="noopener noreferrer">Cómo llegar</a>}
                    <a href="/mis-descuentos" onClick={e => { e.preventDefault(); navigate('/mis-descuentos'); }}>Ver descuentos</a>
                    <button className="lugar-recomendado-cerrar" onClick={() => setLugarCerrado(true)} aria-label="Cerrar">✕</button>
                  </div>
                )}
                <div className="chat-input-area">
                  <textarea
                    className="chat-input"
                    placeholder={`Escribile a ${selected.partner?.name}...`}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    rows={1}
                    maxLength={1000}
                    disabled={sending}
                  />
                  <button className="chat-send-btn" onClick={sendMessage} disabled={!input.trim() || sending}>
                    ➤
                  </button>
                </div>
                </>
              )}
            </>
          )}
        </main>
      </div>

      {/* GIFT MODAL */}
      {regalarOpen && selected && !isBlocked && (
        <RegalarModal match={selected} onClose={() => setRegalarOpen(false)} onError={error} />
      )}

      {perfilOpen && selected?.partner && (
        <PerfilCompleto key={selected.partner.id} profile={selected.partner} cargarId={selected.partner.id} onClose={() => setPerfilOpen(false)} />
      )}

      {citaOpen && selected && (
        <CitaSeguraModal match={selected} onClose={() => setCitaOpen(false)} onError={error} />
      )}

      {/* BLOCK MODAL */}
      {blockConfirm && (
        <div className="modal-overlay" onClick={() => setBlockConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: 'var(--font-display)', marginBottom: 12 }}>🚫 Bloquear a {selected?.partner?.name}</h2>
            <p style={{ color: 'var(--texto-suave)', marginBottom: 20, lineHeight: 1.6 }}>
              Si bloqueás a {selected?.partner?.name}, ya no vas a ver su perfil y no van a poder escribirse más mensajes.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setBlockConfirm(false)}>Cancelar</button>
              <button className="btn btn-primary" style={{ background: 'var(--rojo, #e53935)' }} onClick={handleBlock}>Confirmar bloqueo</button>
            </div>
          </div>
        </div>
      )}

      {/* UNBLOCK MODAL */}
      {unblockConfirm && (
        <div className="modal-overlay" onClick={() => setUnblockConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: 'var(--font-display)', marginBottom: 12 }}>✅ Desbloquear a {selected?.partner?.name}</h2>
            <p style={{ color: 'var(--texto-suave)', marginBottom: 20, lineHeight: 1.6 }}>
              Si desbloqueás a {selected?.partner?.name}, van a poder volver a enviarse mensajes.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setUnblockConfirm(false)}>Cancelar</button>
              <button className="btn btn-primary" style={{ background: '#2d7a2d' }} onClick={handleUnblock}>Confirmar desbloqueo</button>
            </div>
          </div>
        </div>
      )}

      {/* REPORT MODAL */}
      {reportOpen && (
        <div className="modal-overlay" onClick={() => setReportOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: 'var(--font-display)', marginBottom: 12 }}>⚠️ Reportar a {selected?.partner?.name}</h2>
            <p style={{ color: 'var(--texto-suave)', marginBottom: 12, lineHeight: 1.6 }}>
              Seleccioná el motivo del reporte:
            </p>
            <select
              value={reportReason}
              onChange={e => setReportReason(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', marginBottom: 20, borderRadius: 8, border: '1px solid var(--borde)', background: 'var(--fondo-card)', color: 'var(--texto)', fontSize: 14 }}
            >
              <option>Comportamiento inapropiado</option>
              <option>Acoso o mensajes ofensivos</option>
              <option>Perfil falso o spam</option>
              <option>Contenido explícito no deseado</option>
              <option>Violencia o amenazas</option>
              <option>Otro</option>
            </select>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setReportOpen(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleReport}>Confirmar reporte</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
